import { BasketCard } from '../../components/BasketCard';
import { Divider } from '../../components/Divider';
import { PriceDisplay } from '../../components/PriceDisplay';
import { ViewToggle } from '../../components/ViewToggle';
import type { ViewToggleOption } from '../../components/ViewToggle';
import { ItineraryView } from '../ItineraryView';
import { CalendarIcon } from '../../icons/CalendarIcon';
import { CheckIcon } from '../../icons/CheckIcon';
import { ListIcon } from '../../icons/ListIcon';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  ConfirmationCheck,
  ConfirmationRow,
  DiscountAmount,
  ItemsList,
  SummaryLabel,
  SummaryLine,
  TitleRow,
  TitleText,
  TotalLabel,
  TotalLine,
  TotalRow,
  Wrapper,
} from './BasketContents.styles';
import type {
  BasketContentsLabels,
  BasketContentsProps,
  BasketContentsView,
} from './BasketContents.types';

const DEFAULT_LABELS: Required<BasketContentsLabels> = {
  title: 'Your basket',
  itemisedView: 'Itemised view',
  itineraryView: 'Itinerary view',
  addedConfirmation: 'Added to basket',
  subtotal: 'Subtotal',
  promotions: 'Promotions',
  vouchers: 'Vouchers',
  total: 'Total',
  removeLabel: 'Remove',
  noImageLabel: 'No image',
  selectGuestLabel: 'Select guest',
};

export function BasketContents({
  items,
  totalPrice,
  view = 'itemised',
  onViewChange,
  guests,
  showAddedConfirmation = false,
  onRemoveItem,
  heading,
  showTitle = true,
  discountSlot,
  subtotal,
  promotionsDiscount,
  vouchersDiscount,
  locale,
  labels,
  className,
}: BasketContentsProps) {
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const hasGuests = guests !== undefined && guests.length > 0;
  const canToggleView = Boolean(onViewChange && hasGuests);
  const showItinerary = view === 'itinerary' && hasGuests;

  const toggleOptions: ViewToggleOption<BasketContentsView>[] = [
    { value: 'itemised', label: resolvedLabels.itemisedView, Icon: ListIcon },
    { value: 'itinerary', label: resolvedLabels.itineraryView, Icon: CalendarIcon },
  ];

  return (
    <Wrapper className={className} data-testid="basket-contents">
      {(showTitle || heading || canToggleView) && (
        <TitleRow>
          {showTitle && <TitleText>{heading ?? resolvedLabels.title}</TitleText>}
          {canToggleView && onViewChange && (
            <ViewToggle<BasketContentsView>
              value={view}
              onChange={onViewChange}
              options={toggleOptions}
            />
          )}
        </TitleRow>
      )}

      {showAddedConfirmation && (
        <ConfirmationRow role="status">
          <ConfirmationCheck aria-hidden="true">
            <CheckIcon size={12} />
          </ConfirmationCheck>
          {resolvedLabels.addedConfirmation}
        </ConfirmationRow>
      )}

      {showItinerary && guests ? (
        <ItineraryView guests={guests} selectGuestLabel={resolvedLabels.selectGuestLabel} />
      ) : (
        <ItemsList>
          {items.map((item) => (
            <BasketCard
              key={item.id}
              {...item}
              locale={locale}
              noImageLabel={resolvedLabels.noImageLabel}
              removeLabel={resolvedLabels.removeLabel}
              onRemove={onRemoveItem ? () => onRemoveItem(item.id) : undefined}
            />
          ))}
        </ItemsList>
      )}

      {discountSlot}

      <TotalRow>
        <Divider />
        {subtotal && (
          <SummaryLine>
            <SummaryLabel>{resolvedLabels.subtotal}</SummaryLabel>
            <PriceDisplay
              amount={subtotal.amount}
              currencyCode={subtotal.currencyCode}
              locale={locale}
              size="base"
              weight="normal"
            />
          </SummaryLine>
        )}
        {promotionsDiscount && (
          <SummaryLine>
            <SummaryLabel>{resolvedLabels.promotions}</SummaryLabel>
            <DiscountAmount>
              −
              {formatCurrency(promotionsDiscount.amount, promotionsDiscount.currencyCode, {
                locale,
              })}
            </DiscountAmount>
          </SummaryLine>
        )}
        {vouchersDiscount && (
          <SummaryLine>
            <SummaryLabel>{resolvedLabels.vouchers}</SummaryLabel>
            <DiscountAmount>
              −{formatCurrency(vouchersDiscount.amount, vouchersDiscount.currencyCode, { locale })}
            </DiscountAmount>
          </SummaryLine>
        )}
        <TotalLine>
          <TotalLabel>{resolvedLabels.total}</TotalLabel>
          <PriceDisplay
            amount={totalPrice.amount}
            currencyCode={totalPrice.currencyCode}
            locale={locale}
            size="base"
            weight="bold"
          />
        </TotalLine>
      </TotalRow>
    </Wrapper>
  );
}
